import React, { useEffect } from 'react'
import { Check, Clock3, LockKeyhole, Sparkles, Users, X } from 'lucide-react'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import useUiOverlayStore from '../../../store/useUiOverlayStore'
import { formatAgo } from '../utils/profileFormat'
import { formatOwnersPercent, getProgressView, getTierStyle } from '../utils/achievementFormat'

const AchievementModal = ({ achievement, onClose, language }) => {
  const { t } = useTranslation()

  useEffect(() => {
    if (!achievement) return undefined
    const { pushOverlay, popOverlay } = useUiOverlayStore.getState()
    pushOverlay()
    document.body.style.overflow = 'hidden'
    const handleKey = (event) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKey)
    return () => {
      popOverlay()
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handleKey)
    }
  }, [achievement, onClose])

  if (!achievement) return null

  const tier = getTierStyle(achievement.tier)
  const progress = getProgressView(achievement, t, language)

  return (
    <div onClick={onClose} className="fixed inset-0 z-[70] flex overflow-y-auto bg-dark-primary/95 p-4">
      <motion.div
        onClick={(event) => event.stopPropagation()}
        initial={{ opacity: 0, scale: 0.94, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className={`relative m-auto w-full max-w-sm overflow-hidden rounded-[28px] border bg-surface p-6 ${tier.border}`}
        style={{ boxShadow: `0 28px 80px -28px ${tier.glow}` }}
      >
        <div className="pointer-events-none absolute left-1/2 top-0 h-48 w-48 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl" style={{ backgroundColor: tier.glow }} />
        <button type="button" onClick={onClose} className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 active:scale-95" aria-label={t('common.close')}>
          <X size={17} />
        </button>

        <div className="relative text-center">
          <img src={achievement.image} alt={achievement.name} className={`mx-auto h-40 w-40 object-contain drop-shadow-[0_18px_24px_rgba(0,0,0,0.5)] ${achievement.earned ? '' : 'grayscale opacity-45'}`} />
          <div className={`mt-2 text-[10px] font-bold uppercase tracking-[0.2em] ${tier.text}`}>
            {t(`achievements.tiers.${achievement.tier}`, { defaultValue: achievement.tier })}
          </div>
          <h2 className="mt-1 text-2xl font-black not-italic text-white">{achievement.name}</h2>
          <p className="mt-2 text-sm leading-relaxed text-text-secondary">{achievement.description}</p>
        </div>

        <div className="relative mt-5 space-y-2.5">
          <div className="flex items-start gap-3 rounded-xl border border-white/5 bg-black/20 p-3">
            <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${achievement.earned ? 'bg-accent-primary/10 text-accent-primary' : 'bg-white/5 text-text-secondary'}`}>
              {achievement.earned ? <Check size={15} /> : <LockKeyhole size={14} />}
            </span>
            <div className="min-w-0 text-sm">
              <div className="font-semibold text-white">
                {achievement.earned ? t('achievements.earned') : t('achievements.locked')}
              </div>
              {achievement.earned && achievement.earned_ago != null && (
                <div className="mt-0.5 flex items-center gap-1 text-xs text-text-secondary">
                  <Clock3 size={12} />
                  {t('achievements.earnedAgo', { ago: formatAgo(achievement.earned_ago, t) })}
                </div>
              )}
            </div>
          </div>

          {!achievement.earned && progress && (
            <div className="rounded-xl border border-white/5 bg-black/20 p-3">
              <div className="mb-2 flex items-center gap-2 text-xs text-text-secondary">
                <Sparkles size={13} className="text-info" />
                <span>{progress.label}</span>
              </div>
              {progress.showBar && (
                <div className="h-2 overflow-hidden rounded-full bg-white/5">
                  <div className="h-full rounded-full bg-gradient-to-r from-info to-accent-secondary" style={{ width: `${progress.ratio * 100}%` }} />
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 rounded-xl border border-white/5 bg-black/20 p-3 text-xs text-text-secondary">
            <Users size={14} className={tier.text} />
            {t('achievements.owners', { percent: formatOwnersPercent(achievement.owners_pct, language) })}
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default AchievementModal
